import React, { useState } from 'react';
import CenteredLayout from '../../layout/CenteredLayout';
import { AppSearchDropdown, SearchResultItem, SearchCardItem } from '../../customComponents/AppSearchDropdown';
import { AppDropdownField, DropdownOption } from '../../customComponents/AppDropdownField';

// 예시 데이터
const mockSearchResults: SearchCardItem[] = [
  {
    id: 1,
    title: "서울특별시 강남구",
    subtitle: "Seoul Gangnam-gu",
    description: "대한민국의 수도 서울특별시의 자치구"
  },
  {
    id: 2,
    title: "서울특별시 서초구",
    subtitle: "Seoul Seocho-gu", 
    description: "강남 지역의 중심 업무지구"
  },
  {
    id: 3,
    title: "서울특별시 송파구",
    subtitle: "Seoul Songpa-gu",
    description: "올림픽공원과 잠실이 위치한 지역"
  }
];

const dropdownOptions: DropdownOption[] = [
  { value: 'option1', label: '옵션 1' },
  { value: 'option2', label: '옵션 2' },
  { value: 'option3', label: '옵션 3' },
  { value: 'option4', label: '옵션 4' },
];

export const DropdownTestPage: React.FC = () => {
  // 언어 상태
  const [language, setLanguage] = useState<'ko' | 'en'>('ko');
  
  // 검색 드롭다운 상태
  const [selectedValue, setSelectedValue] = useState(''); // 선택된 값 표시용
  const [searchTerm, setSearchTerm] = useState(''); // 검색어
  const [searchResults, setSearchResults] = useState<SearchResultItem[]>([]);
  const [searchLoading, setSearchLoading] = useState(false);
  const [selectedItem, setSelectedItem] = useState<SearchResultItem | null>(null);
  
  // Validation 상태
  const [isValidSelection, setIsValidSelection] = useState(true);
  const [showValidationError, setShowValidationError] = useState(false);

  // 일반 드롭다운 상태
  const [dropdownValue, setDropdownValue] = useState('');

  // 검색 핸들러 (API 호출 시뮬레이션)
  const handleSearch = async (searchTerm: string) => {
    setSearchLoading(true);
    
    // API 호출 시뮬레이션
    setTimeout(() => {
      const filteredResults = mockSearchResults.filter(item =>
        item.title.toLowerCase().includes(searchTerm.toLowerCase()) ||
        item.subtitle?.toLowerCase().includes(searchTerm.toLowerCase()) ||
        item.description?.toLowerCase().includes(searchTerm.toLowerCase())
      );
      setSearchResults(filteredResults);
      setSearchLoading(false);
    }, 1000);
  };

  // 검색어 변경 핸들러
  const handleSearchTermChange = (term: string) => {
    setSearchTerm(term);
    setSearchResults([]); // 검색어 변경 시 이전 결과 초기화
    
    // 검색어가 변경되면 현재 선택된 값이 더 이상 유효하지 않을 수 있음
    if (selectedValue && selectedValue !== term) {
      setIsValidSelection(false);
    }
  };

  // 아이템 선택 핸들러
  const handleSelectItem = (item: SearchResultItem) => {
    setSelectedItem(item);
    setIsValidSelection(true); // 드롭다운에서 선택한 값은 유효함
    setShowValidationError(false); // 에러 메시지 숨김
    
    if (typeof item === 'object' && item !== null && 'title' in item) {
      setSelectedValue(item.title);
    } else if (typeof item === 'object' && item !== null && 'label' in item) {
      setSelectedValue(item.label);
    } else {
      setSelectedValue(String(item));
    }
  };

  // 선택 해제 핸들러
  const handleClear = () => {
    setSelectedValue('');
    setSelectedItem(null);
    setSearchTerm('');
    setSearchResults([]);
    setIsValidSelection(true); // 빈 값은 validation 통과로 처리 (필수값 검증은 별도)
    setShowValidationError(false);
  };

  // 폼 제출 시뮬레이션
  const handleSubmit = () => {
    // 선택된 값이 있지만 유효하지 않은 경우
    if (selectedValue && !isValidSelection) {
      setShowValidationError(true);
      alert('폼 제출 실패: 유효한 검색 결과를 선택해주세요.');
      return;
    }
    
    // 값이 비어있는 경우 (필수값 검증)
    if (!selectedValue) {
      setShowValidationError(true);
      alert('폼 제출 실패: 지역을 선택해주세요.');
      return;
    }
    
    // 성공적으로 제출
    alert(`폼 제출 성공!\n선택된 지역: ${selectedValue}\n선택된 아이템: ${JSON.stringify(selectedItem)}`);
    setShowValidationError(false);
  };

  return (
    <CenteredLayout>
      <div style={{ padding: '20px', maxWidth: '600px', margin: '0 auto' }}>
        <h1>드롭다운 컴포넌트 테스트</h1>
        
        {/* 언어 선택기 */}
        <div style={{ marginBottom: '20px', padding: '10px', background: '#f5f5f5', borderRadius: '8px' }}>
          <label>언어 선택: </label>
          <select 
            value={language} 
            onChange={(e) => setLanguage(e.target.value as 'ko' | 'en')}
            style={{ marginLeft: '10px', padding: '5px' }}
          >
            <option value="ko">한국어</option>
            <option value="en">English</option>
          </select>
        </div>
        
        <div style={{ marginBottom: '40px' }}>
          <h2>검색 가능한 드롭다운</h2>
          <p>텍스트를 입력하고 검색 버튼을 클릭하거나 Enter를 눌러 검색하세요. 결과를 클릭하여 선택할 수 있습니다.</p>
          <AppSearchDropdown
            selectedValue={selectedValue}
            searchTerm={searchTerm}
            onSearchTermChange={handleSearchTermChange}
            onSearch={handleSearch}
            onSelectItem={handleSelectItem}
            onClear={handleClear}
            results={searchResults}
            loading={searchLoading}
            language={language}
            label={language === 'ko' ? "지역 검색" : "Region Search"}
            placeholder={language === 'ko' ? "지역명을 입력하세요..." : "Enter region name..."}
            emptyMessage={language === 'ko' ? "검색된 지역이 없습니다." : "No regions found."}
            isValidSelection={isValidSelection}
            showValidationError={showValidationError}
            validationErrorMessage={language === 'ko' ? "유효한 지역을 선택해주세요." : "Please select a valid region."}
          />
          
          {selectedValue && (
            <div style={{ marginTop: '10px', padding: '10px', background: '#f0f0f0', borderRadius: '8px' }}>
              <strong>선택된 값:</strong> {selectedValue}
            </div>
          )}
          
          {searchTerm && (
            <div style={{ marginTop: '10px', padding: '10px', background: '#e8f5e8', borderRadius: '8px' }}>
              <strong>검색어:</strong> {searchTerm}
            </div>
          )}
          
          {selectedItem && (
            <div style={{ marginTop: '10px', padding: '10px', background: '#e8f5e8', borderRadius: '8px' }}>
              <strong>선택된 아이템:</strong> {JSON.stringify(selectedItem, null, 2)}
            </div>
          )}
          
          {/* 제출 버튼 */}
          <div style={{ marginTop: '20px' }}>
            <button 
              onClick={handleSubmit}
              style={{
                padding: '12px 24px',
                backgroundColor: '#007bff',
                color: 'white',
                border: 'none',
                borderRadius: '8px',
                cursor: 'pointer',
                fontSize: '16px',
                fontWeight: '600'
              }}
            >
              폼 제출
            </button>
          </div>
          
          {/* 디버깅 정보 */}
          <div style={{ marginTop: '15px', padding: '10px', background: '#f8f9fa', borderRadius: '8px', fontSize: '12px' }}>
            <div><strong>디버깅 정보:</strong></div>
            <div>- 선택된 값: "{selectedValue}"</div>
            <div>- 유효한 선택: {isValidSelection ? 'Yes' : 'No'}</div>
            <div>- Validation 에러 표시: {showValidationError ? 'Yes' : 'No'}</div>
          </div>
        </div>

        <div style={{ marginBottom: '40px' }}>
          <h2>일반 선택 드롭다운</h2>
          <p>미리 정의된 옵션 중에서 선택하세요.</p>
          <AppDropdownField
            value={dropdownValue}
            onChange={setDropdownValue}
            options={dropdownOptions}
            language={language}
            label={language === 'ko' ? "옵션 선택" : "Select Option"}
            placeholder={language === 'ko' ? "옵션을 선택해주세요" : "Please select an option"}
          />
          {dropdownValue && (
            <div style={{ marginTop: '10px', padding: '10px', background: '#f0f0f0', borderRadius: '8px' }}>
              <strong>선택된 값:</strong> {dropdownValue}
            </div>
          )}
        </div>
      </div>
    </CenteredLayout>
  );
};
